// Planos do Teia: o que cada um libera e quanto custa. Fonte única para o servidor
// (que cobra e aplica os limites) e para as telas (que mostram).
//
// O que já era aberto continua aberto e sem cadastro: busca, ficha, perfil de pessoa,
// rede societária e o servidor MCP. Os planos pagos acrescentam o que custa manter de
// pé: acompanhar empresas todo dia, consultar em lote e um ritmo maior na API.
//
// PURO: sem import de servidor, para o navegador e os testes importarem.

export type PlanSlug = "gratis" | "pro" | "empresa";
export type BillingCycle = "monthly" | "annual";

export const PLAN_SLUGS: readonly PlanSlug[] = ["gratis", "pro", "empresa"];
export const BILLING_CYCLES: readonly BillingCycle[] = ["monthly", "annual"];

export type PlanLimits = {
  /** Empresas acompanhadas ao mesmo tempo. */
  monitoramento: number;
  /** CNPJs por dia na consulta em lote. 0 = sem lote. */
  loteDia: number;
  /** Chaves de API ativas. 0 = a API segue aberta, no ritmo anônimo. */
  chavesApi: number;
  /** Ritmo da chave de API, em consultas por minuto (também é o pico acumulável). */
  apiPorMinuto: number;
};

export type Plan = {
  slug: PlanSlug;
  nome: string;
  resumo: string;
  /** Preço de tabela em centavos. 0 no plano grátis. */
  precoMensalCentavos: number;
  precoAnualCentavos: number;
  /** Dias de teste na primeira assinatura de cada conta. */
  trialDays: number;
  destaque?: boolean;
  limites: PlanLimits;
};

/** Ritmo da API e do MCP sem chave nenhuma (server/ratelimit.ts, camada "api"). */
export const API_ANONIMA_POR_MINUTO = 60;

export const PLANS: readonly Plan[] = [
  {
    slug: "gratis",
    nome: "Grátis",
    resumo: "Para consultar quando precisar. A busca não pede nem cadastro.",
    precoMensalCentavos: 0,
    precoAnualCentavos: 0,
    trialDays: 0,
    limites: { monitoramento: 3, loteDia: 0, chavesApi: 0, apiPorMinuto: API_ANONIMA_POR_MINUTO },
  },
  {
    slug: "pro",
    nome: "Pro",
    resumo: "Para quem acompanha empresas toda semana: compliance, jurídico, crédito e vendas.",
    precoMensalCentavos: 4900,
    precoAnualCentavos: 49000,
    trialDays: 7,
    destaque: true,
    limites: { monitoramento: 50, loteDia: 500, chavesApi: 3, apiPorMinuto: 180 },
  },
  {
    slug: "empresa",
    nome: "Empresa",
    resumo: "Para equipes e integrações: carteiras grandes, planilhas inteiras e sistemas próprios.",
    precoMensalCentavos: 19900,
    precoAnualCentavos: 199000,
    trialDays: 7,
    limites: { monitoramento: 500, loteDia: 5000, chavesApi: 10, apiPorMinuto: 600 },
  },
];

export function isPlanSlug(value: unknown): value is PlanSlug {
  return typeof value === "string" && (PLAN_SLUGS as readonly string[]).includes(value);
}

export function isCycle(value: unknown): value is BillingCycle {
  return typeof value === "string" && (BILLING_CYCLES as readonly string[]).includes(value);
}

export function getPlan(slug: PlanSlug): Plan {
  return PLANS.find((plan) => plan.slug === slug) ?? PLANS[0];
}

export function isPaid(plan: Plan): boolean {
  return plan.precoMensalCentavos > 0 || plan.precoAnualCentavos > 0;
}

export function priceOf(plan: Plan, cycle: BillingCycle): number {
  return cycle === "annual" ? plan.precoAnualCentavos : plan.precoMensalCentavos;
}

/** Quantos meses o ciclo anual dá de presente em relação a doze mensalidades. */
export function annualSavingMonths(plan: Plan): number {
  if (!plan.precoMensalCentavos) return 0;
  return Math.max(0, Math.round(12 - plan.precoAnualCentavos / plan.precoMensalCentavos));
}

const inteiro = (value: number) => value.toLocaleString("pt-BR");

/** Quantas vezes o ritmo da chave supera o da API aberta. */
export function apiMultiplier(plan: Plan): number {
  return Math.round(plan.limites.apiPorMinuto / API_ANONIMA_POR_MINUTO);
}

/**
 * As frases da página de planos, na ordem. O aviso por e-mail só aparece quando o
 * servidor tem envio de e-mail configurado: sem ele as mudanças ficam na conta, e
 * prometer e-mail seria mentir na vitrine.
 */
export function planHighlights(plan: Plan, ambiente: { email: boolean }): string[] {
  const avisos = ambiente.email ? "Aviso por e-mail quando algo muda" : "Mudanças registradas na sua conta";
  if (plan.slug === "gratis") {
    return [
      "Busca, ficha, perfil e rede societária sem cadastro",
      "Servidor MCP aberto para a sua IA",
      `Acompanhe até ${inteiro(plan.limites.monitoramento)} empresas`,
      avisos,
    ];
  }
  const anterior = plan.slug === "pro" ? "Tudo do Grátis" : "Tudo do Pro";
  return [
    anterior,
    `Acompanhe até ${inteiro(plan.limites.monitoramento)} empresas, com ${avisos.charAt(0).toLowerCase()}${avisos.slice(1)}`,
    `Consulta em lote: até ${inteiro(plan.limites.loteDia)} CNPJs por dia, com planilha CSV`,
    `Chave de API: ${inteiro(plan.limites.apiPorMinuto)} consultas por minuto (${apiMultiplier(plan)}x o ritmo aberto)`,
    `Até ${inteiro(plan.limites.chavesApi)} chaves de API ativas`,
  ];
}

export function formatBRL(cents: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}
